/**
 * ViewportGuard Component
 *
 * Enforces minimum viewport width for Business Web (768px per platform guidelines).
 * Shows a "use a larger screen" prompt for users on smaller screens.
 *
 * Per Tonnie Platform TECH Guidelines:
 * - Business Web minimum viewport: 768px (md breakpoint)
 * - Mobile/small screens see a friendly prompt to use a larger screen
 */

import { Monitor } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from '@tonnie/locale';

interface ViewportGuardProps {
  minWidth?: number;
  children: ReactNode;
  fallback?: ReactNode;
}

function useViewportWidth(): number {
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return 1024;
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

function SmallScreenMessage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-linear-to-b from-primary/5 to-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10">
            <Monitor className="w-10 h-10 text-primary-accessible" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-3">
          {t('business.shell.viewportGuard.title')}
        </h1>

        <p className="text-muted-foreground mb-8 leading-relaxed">
          {t('business.shell.viewportGuard.description')}
        </p>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Monitor className="w-4 h-4" />
            <span>{t('business.shell.viewportGuard.hint')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ViewportGuard({ minWidth = 768, children, fallback }: ViewportGuardProps) {
  const viewportWidth = useViewportWidth();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsChecking(false), 50);
    return () => clearTimeout(timer);
  }, []);

  if (isChecking) {
    return null;
  }

  if (viewportWidth < minWidth) {
    return <>{fallback || <SmallScreenMessage />}</>;
  }

  return <>{children}</>;
}

export default ViewportGuard;
